/**
 * Thumbnail Capture Utilities
 *
 * RTT-based thumbnail generation for editor use cases: scene thumbnails,
 * asset thumbnails, live publish previews, and image thumbnail generation.
 *
 * All functions accept Babylon.js scene/camera objects directly rather than
 * querying the DOM, making them framework-agnostic and testable.
 */

import { Color4 } from '@babylonjs/core/Maths/math.color';
import { RenderTargetTexture } from '@babylonjs/core/Materials/Textures/renderTargetTexture';
import { Constants } from '@babylonjs/core/Engines/constants';
import { ImageProcessingPostProcess } from '@babylonjs/core/PostProcesses/imageProcessingPostProcess';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { Vector3, Matrix, Quaternion } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import type { ObjectRenderer } from '@babylonjs/core/Rendering/objectRenderer';
import type { SceneObject } from '../core/SceneObject';
import { Properties } from '../behaviors/properties';
import { Renderer } from '../behaviors/renderer';
import { textCaptureSourcesForScene, type TextCaptureSource } from '../behaviors/text/textCaptureRegistry';
import { Logger } from '../utils/Logger';
import { downscaleRgba, flipRows } from './thumbnailFilter';
import { captureFrameOf, cropFramePixels, framedDistance, viewOrbit, type CaptureFrame } from './captureFrame';
import { prewarmStepOffsetSpanningLifetime } from '../utils/ParticlePrewarm';

/**
 * Babylon's `RenderingGroup._renderParticles` only draws a particle system into
 * a render target when the system's emitter node is a member of the RTT's
 * `activeMeshes` (= `rtt.renderList`), unless the emitter is a positionless
 * Vector3 or no list is supplied. Our capture RTTs always build an explicit
 * `renderList` (to isolate content), and the ParticleSystem behavior sets its
 * emitter to the SceneObject's root TransformNode — which is never in a list
 * built from `getChildMeshes()` or `scene.meshes`. The emitter is therefore
 * dispatched but never drawn, so particles vanish from thumbnails.
 *
 * We can't just push the emitter TransformNode onto the render list: the
 * render-manager's *mesh* loop treats every renderList entry as an AbstractMesh
 * (LOD/`isReady`/material lookups), and a bare TransformNode lacks that surface
 * (`getMaterialForRenderPass is not a function`). Instead, for each in-scope
 * node emitter we create a geometry-less proxy `Mesh` parented at the emitter
 * (identical world matrix → same emission origin), re-point the system at the
 * proxy, and add the proxy to the render list. The proxy has no geometry so it
 * draws nothing itself; it exists only to satisfy the draw-gate's membership
 * check. The returned cleanup restores every emitter and disposes the proxies.
 *
 * `inScope` selects which emitters belong to this capture (whole-scene vs. a
 * single subtree).
 *
 * Returns the in-scope particle systems (so the caller can prewarm them inside
 * the synchronous pose window — see {@link prewarmParticleSystems}) plus a
 * `restore` that re-points every emitter and disposes the proxies.
 */
function makeParticleEmittersRenderable(
    scene: Scene,
    renderList: AbstractMesh[],
    inScope: (emitter: TransformNode) => boolean,
): { systems: ParticleSystemLike[]; restore: () => void } {
    const swaps: { ps: ParticleSystemLike; original: unknown; proxy: Mesh }[] = [];
    for (const ps of scene.particleSystems) {
        const emitter = ps.emitter;
        // The draw-gate keys off `emitter.position`: positionless Vector3
        // emitters already pass it, so only node emitters (which carry a
        // `position` and `isDescendantOf`) need the proxy. Duck-type rather than
        // `instanceof` (works across AbstractMesh / TransformNode).
        if (!emitter || typeof emitter !== 'object') continue;
        if (!('position' in emitter) || typeof (emitter as TransformNode).isDescendantOf !== 'function') continue;
        const node = emitter as TransformNode;
        if (!inScope(node)) continue;

        const proxy = new Mesh('__particleEmitterProxy__', scene);
        proxy.parent = node;            // identical world matrix → unchanged emission
        proxy.isVisible = false;        // no geometry anyway; never draws
        proxy.isPickable = false;
        const psLike = ps as unknown as ParticleSystemLike;
        swaps.push({ ps: psLike, original: emitter, proxy });
        psLike.emitter = proxy;
        renderList.push(proxy);
    }
    return {
        systems: swaps.map((s) => s.ps),
        restore: () => {
            for (const s of swaps) {
                s.ps.emitter = s.original;
                s.proxy.dispose();
            }
        },
    };
}

/**
 * Minimal structural view of a Babylon (CPU or GPU) particle system — just the
 * members the capture rig touches. Avoids a value import of ParticleSystem and
 * works across CPU/GPU variants.
 */
interface ParticleSystemLike {
    emitter: unknown;
    updateSpeed: number;
    preWarmStepOffset: number;
    isStarted?: () => boolean;
    animate?: (preWarmOnly?: boolean) => void;
    reset?: () => void;
    getActiveCount?: () => number;
    maxLifeTime?: number;
}

/** Number of synchronous prewarm steps. Bounded + cheap (no rendering). */
const PREWARM_STEPS = 120;

/**
 * Fast-forward already-started particle systems to a steady-state cloud before a
 * single-frame capture. Thumbnails render exactly one frame, so a freshly-started
 * emitter would otherwise capture only the handful of particles emitted during the
 * RTT readiness wait. `animate(true)` is Babylon's own prewarm step (the same call
 * `start()` runs `preWarmCycles` times); each advances `updateSpeed *
 * preWarmStepOffset` seconds, emitting + aging particles, and bypasses the
 * per-frame render-id guard so it can be stepped in a synchronous burst.
 *
 * MUST be called inside the synchronous pose window (after the pose is applied),
 * so particles emit from the *posed* emitter position. We temporarily widen
 * `preWarmStepOffset` so PREWARM_STEPS steps span ~the longest particle lifetime
 * (the cloud reaches equilibrium), then restore it. Returns a restore for any
 * mutated fields. No-op for systems that aren't started or lack `animate`.
 *
 * When `posed` is true we `reset()` first to discard the live cloud. Emitted
 * particles live in WORLD space and do NOT follow the emitter when it moves, so a
 * posed capture (keyframe / state preview) that kept the live particles would
 * show the cloud at the *base* position while meshes posed correctly (the
 * mispositioning bug). After reset, every particle is freshly emitted from the
 * posed emitter, so the cloud sits where the pose puts it. For a NON-posed
 * capture we must NOT reset: the live cloud is already correctly positioned and
 * spread by real-time emission, and resetting it (then framing the camera on a
 * not-yet-repopulated bound) races the auto-frame and can blank the thumbnail.
 */
function prewarmParticleSystems(systems: ParticleSystemLike[], posed: boolean): () => void {
    const restores: Array<() => void> = [];
    for (const ps of systems) {
        if (typeof ps.animate !== 'function') continue;
        if (ps.isStarted && !ps.isStarted()) continue;
        // Posed capture only: drop world-space particles emitted at the pre-pose
        // position so the rebuilt cloud emits entirely from the posed emitter.
        if (posed) ps.reset?.();
        const savedOffset = ps.preWarmStepOffset;
        // Span roughly one max-lifetime across PREWARM_STEPS so the oldest
        // particles complete their lifecycle and the cloud reaches steady state.
        ps.preWarmStepOffset = prewarmStepOffsetSpanningLifetime(PREWARM_STEPS, ps.maxLifeTime ?? 0, ps.updateSpeed);
        for (let i = 0; i < PREWARM_STEPS; i++) ps.animate(true);
        restores.push(() => { ps.preWarmStepOffset = savedOffset; });
    }
    return () => { for (const r of restores) r(); };
}

// ============================================================================
// MSDF Text Draw Pass
// ============================================================================

/**
 * Make an RTT render include the scene's live MSDF text.
 *
 * Why this exists: MSDF glyphs are not scene meshes. The `Text` behavior
 * draws them imperatively into the currently bound framebuffer during the
 * live main-camera render (`scene.onAfterCameraRenderObservable` — see
 * `Scene._renderForCamera`). An RTT render (`rtt.render()` →
 * `_renderToTarget` → `ObjectRenderer.render`) never notifies that
 * observable, so no capture RTT in this file ever contained text: thumbnails
 * of text-bearing scenes shipped with meshes but no glyphs.
 *
 * The hook: `ObjectRenderer.onAfterRenderingManagerRenderObservable`, which
 * fires INSIDE the render pass — after the render list's meshes are drawn,
 * while the RTT framebuffer is still bound and its viewport still set. Two
 * alternatives were rejected:
 *
 *  - `rtt.onAfterRenderObservable` ALSO fires (per pass) from
 *    `ObjectRenderer._checkReadiness()`, i.e. on every
 *    `rtt.isReadyForRendering()` poll of the readiness wait — with NO
 *    framebuffer bound, which would spray stray glyph draws into the live
 *    backbuffer. (Its legitimate notify also comes late: from the
 *    `_unbindFrameBuffer` callback, after the MSAA resolve.)
 *  - `scene.onAfterCameraRenderObservable` (what the live path uses) simply
 *    never fires for RTTs — that's the bug.
 *
 * The rendering-manager notify fires only during a real render, so the
 * observer is safe to attach across the readiness wait; and because it runs
 * before `_unbindFrameBuffer`, it is ordered before any
 * `rtt.onAfterRenderObservable` readback observer (the publish preview's
 * `readPixels`) regardless of registration order.
 *
 * Matrices come from the capture camera (`rtt.activeCamera`) — its view and
 * frozen aspect-1 projection — NOT the live camera, whose projection would
 * misplace the glyphs relative to the square-framed meshes.
 *
 * `getSources` is re-evaluated per render so per-frame RTTs (publish
 * preview) track text objects being added/removed; one-shot captures pass a
 * prebuilt list. Empty list → the pass draws nothing and text-free captures
 * are unchanged.
 *
 * Returns a remover; callers MUST invoke it in their teardown.
 */
function addTextDrawPass(
    rtt: RenderTargetTexture,
    camera: Camera,
    getSources: () => readonly TextCaptureSource[],
): () => void {
    // `_objectRenderer` is internal on RenderTargetTexture (only proxied
    // observables are public, and the proxied onAfter fires during readiness
    // checks too — see above). Guard the observable as well as the renderer:
    // callers invoke this OUTSIDE their try/finally (alongside the other
    // rig-setup helpers), so a throw here would leak the rig and reject the
    // capture instead of degrading. A Babylon bump that renames either
    // degrades to text-less thumbnails; the wiring test in
    // test/vitest/editor/thumbnail-text-capture.test.ts pins them.
    const objectRenderer = (rtt as unknown as { _objectRenderer?: ObjectRenderer })._objectRenderer;
    const observable = objectRenderer?.onAfterRenderingManagerRenderObservable;
    if (!observable) {
        Logger.warn('[ThumbnailCapture] RTT object renderer observable missing — text draw pass skipped (Babylon internals changed?)');
        return () => {};
    }
    let warned = false;
    const observer = observable.add(() => {
        const sources = getSources();
        if (sources.length === 0) return;
        const view = camera.getViewMatrix();
        const projection = camera.getProjectionMatrix();
        for (const source of sources) {
            // Contained per source: Babylon observables don't catch observer
            // throws, and on the persistent preview path this runs inside the
            // LIVE scene's render loop — an escaped throw would hit the
            // editor session, not just this capture. Degrade to "this
            // source's text is missing" and keep drawing the rest. Warn once
            // per pass, not per frame (the preview renders every frame).
            try {
                source.renderGlyphs(view, projection);
            } catch (err) {
                if (!warned) {
                    warned = true;
                    Logger.warn('[ThumbnailCapture] text draw failed; continuing without that text:', err);
                }
            }
        }
    });
    return () => {
        observable.remove(observer);
    };
}

/**
 * The scene's text sources whose glyph root sits inside `root`'s subtree —
 * the text analog of the particle `inScope` filter. Node parentage is
 * queried live at call time (registration caches nothing), so captures see
 * post-reparent topology (e.g. `onAnimationRootCreated`).
 */
function textSourcesInSubtree(scene: Scene, root: TransformNode): TextCaptureSource[] {
    return textCaptureSourcesForScene(scene).filter((source) => {
        const node = source.getTextRootNode();
        return node !== null && (node === root || node.isDescendantOf(root));
    });
}

// ============================================================================
// Constants
// ============================================================================

const THUMB_SIZE = 256;
/**
 * Capture renders at this multiple of the stored size, then shrinks with a
 * linear-light Lanczos-3 filter (see thumbnailFilter.ts). The render is capped at
 * 2048 and at the engine's maximum texture size (captureRenderSize).
 */
const SUPERSAMPLE = 4;

/**
 * Cap on the automatic 4x render for object/asset captures. They run per state and
 * per keyframe edit, so the multiple stops at 1024 however large `size` is; a
 * 128 output renders at 512, a 256 output at 1024.
 */
const MAX_SUBTREE_RENDER = 1024;
const PREVIEW_SIZE = 512;

/** Cap on engine frames to wait for an RTT to become renderable. ~2s at 60fps. */
const RTT_READY_MAX_FRAMES = 120;

/** MSAA sample count for capture targets; clamped to what the engine supports. */
const CAPTURE_MSAA_SAMPLES = 4;

/**
 * Make a capture RTT match what the viewer shows at thumbnail size.
 *
 * - Multisampling: a plain single-sample 8-bit target stair-steps every edge.
 *   Samples are set only when the engine reports MSAA support (WebGL2 / WebGPU);
 *   on WebGL1 the target stays single-sample.
 * - Image processing: materials normally apply the scene's tone mapping,
 *   exposure and contrast themselves, and that carries over into the RTT. When
 *   the scene applies it as a camera post-process instead (`applyByPostProcess`),
 *   materials skip it, so the RTT needs its own pass using the scene's
 *   configuration. Adding the pass unconditionally would apply it twice.
 *
 * Returns a cleanup that disposes anything this created.
 */
export function configureCaptureQuality(rtt: RenderTargetTexture, scene: Scene): () => void {
    const engine = scene.getEngine();
    const maxSamples = engine.getCaps().maxMSAASamples ?? 1;
    rtt.samples = Math.max(1, Math.min(CAPTURE_MSAA_SAMPLES, maxSamples));

    const config = scene.imageProcessingConfiguration;
    if (!config?.applyByPostProcess || !config.isEnabled) return () => {};
    const pass = new ImageProcessingPostProcess(
        '__thumbImageProcessing__', 1, null, undefined, engine, false,
        Constants.TEXTURETYPE_UNSIGNED_BYTE, config,
    );
    rtt.addPostProcess(pass);
    return () => {
        rtt.removePostProcess(pass);
        pass.dispose();
        // The post-process clears this flag on the SHARED scene config when it is
        // disposed; put it back so the live viewport doesn't start applying
        // tone mapping twice.
        config.applyByPostProcess = true;
    };
}

// ============================================================================
// RTT Readiness
// ============================================================================

/**
 * Wait until a freshly-created RTT is ready for a single-pass `rtt.render()`,
 * or until `RTT_READY_MAX_FRAMES` engine frames have elapsed (whichever first).
 *
 * Why this is needed: an RTT pass needs its own shader-effect compile distinct
 * from whatever is already compiled for the live camera. Without waiting,
 * `rtt.render()` on a freshly-created RTT silently produces an empty (or
 * partial) framebuffer — Babylon skips meshes whose RTT-pass effects aren't
 * ready, with no error or warning.
 *
 * `material.isReady(mesh)` is NOT a reliable proxy: it can return `false` on
 * the live camera even while the live render is producing the mesh perfectly,
 * and conversely can return `true` for the live camera before the RTT-pass
 * effect has compiled. `rtt.isReadyForRendering()` is the right signal.
 *
 * On timeout we still proceed to render — better a partial thumbnail than a
 * failed save. The timeout is logged so stuck RTTs surface in production.
 *
 * History of approaches tried for this same problem in PreviewRenderer (kept
 * here for context — do not re-litigate without reading the regression test
 * `test/thumbnail-capture.spec.ts`):
 *   1. material.isReady() + onCompiled — false positives
 *   2. setTimeout polling — wasteful, doesn't align with render frames
 *   3. forceCompilationAsync alone — shader compiles but GPU pipeline state
 *      (texture uploads, IBL binds) isn't resolved until after a render
 *   4. forceCompilationAsync + double rtt.render() — synchronous calls
 *      don't finalize GPU state
 *   5. forceCompilationAsync + rAF — one frame isn't always enough on cold load
 *
 * `isContentReady` extends the readiness predicate for content the RTT's own
 * check can't see — `rtt.isReadyForRendering()` reflects only the MESH render
 * list, while e.g. MSDF text draws through a separate effect that compiles on
 * its own schedule (see `Text.isCaptureReady`). Same proceed-on-timeout
 * contract: a still-not-ready extra is skipped, never a failure.
 */
export async function waitForRttReady(
    rtt: RenderTargetTexture,
    scene: Scene,
    label: string,
    isContentReady?: () => boolean,
): Promise<void> {
    const ready = (): boolean => rtt.isReadyForRendering() && (!isContentReady || isContentReady());
    if (ready()) return;
    await new Promise<void>((resolve) => {
        let frames = 0;
        const observer = scene.onBeforeRenderObservable.add(() => {
            frames++;
            if (ready() || frames >= RTT_READY_MAX_FRAMES) {
                scene.onBeforeRenderObservable.remove(observer);
                if (frames >= RTT_READY_MAX_FRAMES) {
                    Logger.warn(`[ThumbnailCapture] RTT '${label}' not ready after ${RTT_READY_MAX_FRAMES} frames, rendering anyway`);
                }
                resolve();
            }
        });
    });
}

// ============================================================================
// Pixel → Image Helpers
// ============================================================================

/**
 * Convert bottom-up RGBA pixels from an RTT readPixels() call into a
 * right-side-up PNG File. Handles both Uint8 and Float32 pixel buffers.
 *
 * When `outSize` is smaller than the source, the image is shrunk with a Lanczos-3
 * filter averaged in linear light and quantised with an ordered dither (see
 * thumbnailFilter.ts), not a canvas `drawImage` bilinear step.
 *
 * @param pixels  - Raw pixel data from RenderTargetTexture.readPixels()
 * @param width   - Pixel width of the source RTT
 * @param height  - Pixel height of the source RTT
 * @param outSize - Output size (square). Clamped to the source size: never upscales.
 */
export async function pixelsToFile(
    pixels: ArrayBufferView,
    width: number,
    height: number,
    outSize = THUMB_SIZE,
): Promise<File | null> {
    return pixelsToSizedFile(pixels, width, height, outSize, outSize);
}

/** Shared conversion for square thumbnails and rectangular object frames. */
async function pixelsToSizedFile(
    pixels: ArrayBufferView,
    width: number,
    height: number,
    outWidth: number,
    outHeight: number,
): Promise<File | null> {
    // Normalise to Uint8ClampedArray
    let src: Uint8ClampedArray;
    if (pixels instanceof Float32Array) {
        src = new Uint8ClampedArray(pixels.length);
        for (let i = 0; i < pixels.length; i++) src[i] = Math.round(Math.min(1, Math.max(0, pixels[i])) * 255);
    } else {
        src = new Uint8ClampedArray(pixels.buffer, pixels.byteOffset, pixels.byteLength);
    }

    // RTT pixels are bottom-up; flip, then shrink if asked to.
    const upright = flipRows(src, width, height);
    const outW = Math.min(outWidth, width);
    const outH = Math.min(outHeight, height);
    const final = downscaleRgba(upright, width, height, outW, outH);

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const imageData = ctx.createImageData(outW, outH);
    imageData.data.set(final);
    ctx.putImageData(imageData, 0, 0);

    const blob = await new Promise<Blob | null>(r => canvas.toBlob(r, 'image/png'));
    if (!blob) return null;
    return new File([blob], 'thumbnail.png', { type: 'image/png' });
}

/**
 * Draw bottom-up RTT pixel data right-side-up into a canvas.
 * Used by the publish preview to paint each frame.
 */
export function drawPixelsToCanvas(pixels: ArrayBufferView, size: number, canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const imageData = ctx.createImageData(size, size);
    // Normalise to Uint8ClampedArray (RTT may return Float32Array with 0-1 range)
    let src: Uint8ClampedArray;
    if (pixels instanceof Float32Array) {
        src = new Uint8ClampedArray(pixels.length);
        for (let i = 0; i < pixels.length; i++) src[i] = Math.round(Math.min(1, Math.max(0, pixels[i])) * 255);
    } else {
        src = new Uint8ClampedArray(pixels.buffer, pixels.byteOffset, pixels.byteLength);
    }
    const dst = imageData.data;
    const stride = size * 4;
    for (let y = 0; y < size; y++) {
        const srcOffset = (size - 1 - y) * stride;
        const dstOffset = y * stride;
        dst.set(src.subarray(srcOffset, srcOffset + stride), dstOffset);
    }
    ctx.putImageData(imageData, 0, 0);
}

/** Hard cap on a capture render (2048² RGBA8 is 16 MB, 4x MSAA four times that). */
const MAX_CAPTURE_RENDER = 2048;

/**
 * Render size for a capture: at least the output size, at most 2048 and the
 * engine's texture limit.
 */
export function captureRenderSize(scene: Scene, requested: number, outSize: number): number {
    const max = Math.min(MAX_CAPTURE_RENDER, scene.getEngine().getCaps().maxTextureSize || 4096);
    return Math.max(Math.min(outSize, max), Math.min(Math.floor(requested), max));
}

// ============================================================================
// Scene Thumbnail Capture
// ============================================================================

/**
 * Runtime-only opt-out: a mesh with `metadata.excludeFromThumbnail === true`
 * stays in the viewport but is left out of scene captures and the publish
 * preview and object captures (e.g. Studio's mirror-texture floor reflection
 * plane, which the capture camera never re-renders). Hosts set it when they
 * create such a mesh.
 */
function isCapturableMesh(mesh: AbstractMesh): boolean {
    if (!mesh.isEnabled() || !mesh.isVisible) return false;
    return (mesh.metadata as { excludeFromThumbnail?: boolean } | null)?.excludeFromThumbnail !== true;
}

/**
 * Frozen square projection for a capture camera. Babylon draws an
 * `ignoreCameraMaxZ` mesh (e.g. a skybox) with an infinite far plane, but only
 * outside intermediate (RTT) rendering, so a capture that freezes the
 * projection at `maxZ` clips it (Mesh.render skips its own infinite far plane while
 * rendering an intermediate target). When any mesh in the list sets the flag,
 * use zfar 0 (infinite) like the main render does.
 */
function captureProjection(fov: number, minZ: number, maxZ: number, meshes: readonly AbstractMesh[], halfZ: boolean): Matrix {
    let infinite = false;
    for (const m of meshes) {
        if ((m as { ignoreCameraMaxZ?: boolean }).ignoreCameraMaxZ === true && isCapturableMesh(m)) {
            infinite = true;
            break;
        }
    }
    return Matrix.PerspectiveFovRH(fov, 1, minZ, infinite ? 0 : maxZ, halfZ);
}

/**
 * Capture a thumbnail of the current scene from the given camera's viewpoint.
 *
 * Uses an RTT with square aspect ratio, transparent background, and only
 * visible content meshes. Returns a File suitable for upload, or undefined
 * if capture fails.
 *
 * Renders at `size` (default 1024, 4x the stored size) with MSAA and the
 * scene's tone mapping (MSAA where the engine has it), then shrinks to
 * THUMB_SIZE with a linear-light filter and ordered dither. The PNG is 256 square,
 * the size the server stores.
 *
 * @param scene   - The Babylon.js scene to capture
 * @param camera  - The camera whose viewpoint to use (typically the editor camera)
 * @param size    - Render size in pixels (capped at 2048 and the engine's max texture
 *                  size, and never below the output size)
 */
export async function captureSceneThumbnail(
    scene: Scene,
    camera: ArcRotateCamera,
    size = THUMB_SIZE * SUPERSAMPLE,
): Promise<File | undefined> {
    const cam = new ArcRotateCamera(
        '__catalogThumbCam__',
        camera.alpha, camera.beta, camera.radius,
        camera.target.clone(), scene,
    );
    cam.fov = camera.fov;
    cam.minZ = camera.minZ;
    cam.maxZ = camera.maxZ;
    cam.detachControl();
    scene.activeCamera = camera; // restore immediately

    const engine = scene.getEngine();

    const renderSize = captureRenderSize(scene, size, THUMB_SIZE);
    const rtt = new RenderTargetTexture(
        '__catalogThumbRtt__',
        { width: renderSize, height: renderSize },
        scene,
        false,
        false,
        Constants.TEXTURETYPE_UNSIGNED_BYTE,
    );
    rtt.activeCamera = cam;
    rtt.clearColor = new Color4(0, 0, 0, 0);
    rtt.renderParticles = true;
    rtt.ignoreCameraViewport = true;
    const disposeQuality = configureCaptureQuality(rtt, scene);

    // Populate render list with visible content meshes
    rtt.renderList = [];
    for (const mesh of scene.meshes) {
        if (isCapturableMesh(mesh)) {
            rtt.renderList.push(mesh);
        }
    }
    // Freeze projection with aspect=1 for the square RTT
    cam.freezeProjectionMatrix(
        captureProjection(cam.fov, cam.minZ, cam.maxZ, rtt.renderList, engine.isNDCHalfZRange),
    );
    // Whole-scene capture: every particle emitter is in scope.
    const { systems: particleSystems, restore: restoreParticles } = makeParticleEmittersRenderable(
        scene, rtt.renderList, () => true,
    );
    // Whole-scene capture: every live text source is in scope (see
    // addTextDrawPass for why RTT captures must draw MSDF text themselves).
    const textSources = textCaptureSourcesForScene(scene);
    const removeTextPass = addTextDrawPass(rtt, cam, () => textSources);

    try {
        await waitForRttReady(rtt, scene, 'captureSceneThumbnail',
            () => textSources.every((s) => s.isCaptureReady()));
        // Seed the particle clouds to steady-state before the single render frame.
        // No pose here, so don't reset (the live cloud is already positioned).
        const restorePrewarm = prewarmParticleSystems(particleSystems, false);
        rtt.render();
        restorePrewarm();
        const pixels = await rtt.readPixels();
        if (!pixels) return undefined;
        return (await pixelsToFile(pixels, renderSize, renderSize)) ?? undefined;
    } catch (err) {
        Logger.error('[ThumbnailCapture] Scene thumbnail capture failed:', err);
        return undefined;
    } finally {
        removeTextPass();
        restoreParticles();
        disposeQuality();
        rtt.dispose();
        cam.dispose();
    }
}

// ============================================================================
// Live Publish Preview
// ============================================================================

/**
 * Create a persistent RTT that renders every frame, tracking the given camera
 * with square aspect and transparent background. Used by publish dialogs to
 * show a live preview of the scene.
 *
 * The `onDraw` callback receives each rendered frame as pixel data so the
 * caller can paint it into a preview canvas.
 *
 * Returns a dispose function that tears everything down.
 *
 * @param scene        - The Babylon.js scene to preview
 * @param sourceCamera - The camera to track (typically the editor camera)
 * @param onDraw       - Callback receiving raw pixels after each render
 * @param size         - Preview size in pixels (default 512)
 */
export function createPublishPreview(
    scene: Scene,
    sourceCamera: ArcRotateCamera,
    onDraw: (pixels: ArrayBufferView, size: number) => void,
    size = PREVIEW_SIZE,
): () => void {
    const rtt = new RenderTargetTexture('publishPreview', size, scene, false, true);
    rtt.clearColor = new Color4(0, 0, 0, 0);
    rtt.renderParticles = true;
    rtt.refreshRate = RenderTargetTexture.REFRESHRATE_RENDER_ONEVERYFRAME;
    rtt.renderList = [];

    // The __ prefix opts this camera into the render loop's self-heal (#645):
    // a throw during this RTT's render parks scene.activeCamera here, and the
    // guard only reclaims from __-prefixed helper cameras (#659).
    const previewCam = new ArcRotateCamera('__publishPreviewCam__', 0, 0, 1, Vector3.Zero(), scene);
    previewCam.detachControl();
    scene.activeCamera = sourceCamera; // Restore editor camera

    const engine = scene.getEngine();
    rtt.activeCamera = previewCam;

    // Sync preview camera from source camera each frame
    rtt.onBeforeBindObservable.add(() => {
        previewCam.alpha = sourceCamera.alpha;
        previewCam.beta = sourceCamera.beta;
        previewCam.radius = sourceCamera.radius;
        previewCam.target.copyFrom(sourceCamera.target);
        previewCam.fov = sourceCamera.fov;
        previewCam.minZ = sourceCamera.minZ;
        previewCam.maxZ = sourceCamera.maxZ;
        previewCam.getViewMatrix(true);
        // The list is rebuilt after this observer (onBeforeRender), so read the live scene meshes.
        const proj = captureProjection(
            sourceCamera.fov, sourceCamera.minZ, sourceCamera.maxZ,
            scene.meshes, engine.isNDCHalfZRange,
        );
        previewCam.freezeProjectionMatrix(proj);
    });

    // Populate render list each frame
    rtt.onBeforeRenderObservable.add(() => {
        const list = rtt.renderList!;
        list.length = 0;
        for (const mesh of scene.meshes) {
            if (isCapturableMesh(mesh)) {
                list.push(mesh);
            }
        }
    });

    scene.customRenderTargets.push(rtt);

    // Draw live MSDF text into each preview frame (see addTextDrawPass). The
    // registry is queried per frame, mirroring the render-list rebuild above,
    // so text objects added/removed while the dialog is open track correctly.
    // The pass fires inside the render (before unbind) and is therefore
    // ordered before the readPixels observer below, which fires at unbind —
    // glyphs are always in the frame that gets read back.
    const removeTextPass = addTextDrawPass(rtt, previewCam, () => textCaptureSourcesForScene(scene));

    // Deliver pixels after each render
    const observer = rtt.onAfterRenderObservable.add(() => {
        void rtt.readPixels(undefined, undefined, undefined, false)?.then(pixels => {
            if (pixels) onDraw(pixels, size);
        });
    });

    return () => {
        removeTextPass();
        rtt.onAfterRenderObservable.remove(observer);
        const idx = scene.customRenderTargets.indexOf(rtt);
        if (idx >= 0) scene.customRenderTargets.splice(idx, 1);
        rtt.dispose();
        previewCam.dispose();
    };
}

// ============================================================================
// Image Thumbnail Generation
// ============================================================================

/**
 * Generate a 256x256 thumbnail from an image Blob by loading it into an
 * offscreen canvas, center-cropping the shorter dimension, and drawing the
 * result at THUMB_SIZE x THUMB_SIZE.
 *
 * Returns a File suitable for upload, or undefined if generation fails.
 */
export async function generateImageThumbnail(blob: Blob): Promise<File | undefined> {
    try {
        const url = URL.createObjectURL(blob);
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = reject;
            el.src = url;
        });
        URL.revokeObjectURL(url);

        // Center-crop to square, then draw at THUMB_SIZE
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2;
        const sy = (img.height - side) / 2;

        const canvas = document.createElement('canvas');
        canvas.width = THUMB_SIZE;
        canvas.height = THUMB_SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) return undefined;

        ctx.drawImage(img, sx, sy, side, side, 0, 0, THUMB_SIZE, THUMB_SIZE);

        const out = await new Promise<Blob | null>(r => canvas.toBlob(r, 'image/png'));
        if (!out) return undefined;
        return new File([out], 'thumbnail.png', { type: 'image/png' });
    } catch (err) {
        Logger.error('[ThumbnailCapture] Image thumbnail generation failed:', err);
        return undefined;
    }
}

// ============================================================================
// Asset Thumbnail Generation
// ============================================================================

/**
 * Minimal interface for importing/removing temporary assets during thumbnail
 * generation. Implement this using your editor's TraceViewer instance.
 */
export interface ThumbnailImporter {
    /** Import a blob into the scene temporarily. Returns the created SceneObject. */
    import(blob: Blob, fileName: string, name?: string): Promise<{ sceneObject: SceneObject }>;
    /** Remove a SceneObject from the scene (cleanup after thumbnail capture). */
    deleteSceneObject(sceneObject: SceneObject): void;
}

/**
 * Generate a thumbnail for an asset file by temporarily importing it into
 * the scene, rendering an isolated RTT capture, then removing it.
 *
 * Returns a File suitable for upload, or undefined if generation fails
 * (callers should proceed without a thumbnail in that case).
 *
 * @param file     - The asset file to generate a thumbnail for
 * @param importer - An object that can import/remove temporary assets
 * @param scene    - The Babylon.js scene to render in
 */
export async function generateAssetThumbnail(
    file: File,
    importer: ThumbnailImporter,
    scene: Scene,
): Promise<File | undefined> {
    let sceneObject: SceneObject | null = null;

    try {
        // Import the asset into the scene
        const result = await importer.import(file, file.name, '__thumb_preview__');
        sceneObject = result.sceneObject;

        // Headless asset preview: fixed 3/4 angle + injected light (scene lighting
        // may not be applied to a freshly-imported temp object yet). Framing angle,
        // distance and clip planes match the pre-refactor behaviour; the one delta is
        // that bounds now come from geometry meshes only (not the full hierarchy via
        // getHierarchyBoundingVectors), so an asset with non-mesh helper/collider
        // children frames slightly tighter — closer to the actual visible content.
        return (await captureTransformSubtree(sceneObject.transform, scene, THUMB_SIZE, 'generateAssetThumbnail', { injectLight: true })) ?? undefined;
    } catch (err) {
        Logger.error('[ThumbnailCapture] Asset thumbnail generation failed:', err);
        return undefined;
    } finally {
        // Remove the temporary asset from the scene
        if (sceneObject) {
            importer.deleteSceneObject(sceneObject);
        }
    }
}

// ============================================================================
// Object Thumbnail Capture (isolated, no live mutation)
// ============================================================================

/**
 * Union the WORLD-space bounding boxes of the given meshes — i.e. framing on
 * EXACTLY what we render, not the whole hierarchy. (`getHierarchyBoundingVectors`
 * would include invisible/helper descendants like a shadow-ground plane or a
 * collider and inflate the bounds, shrinking the content to a dot.) Forces each
 * mesh's world matrix first so the bounds reflect the current (possibly just-posed)
 * transform, since the live render loop may not have ticked. Returns a tiny box at
 * the origin if none of the meshes have geometry.
 */
function computeMeshesWorldBounds(meshes: AbstractMesh[]): { min: Vector3; max: Vector3 } {
    const min = new Vector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
    const max = new Vector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
    let any = false;
    for (const mesh of meshes) {
        if (mesh.getTotalVertices() === 0) continue; // skip geometry-less nodes
        mesh.computeWorldMatrix(true);
        const bb = mesh.getBoundingInfo().boundingBox;
        const lo = bb.minimumWorld;
        const hi = bb.maximumWorld;
        min.x = Math.min(min.x, lo.x); min.y = Math.min(min.y, lo.y); min.z = Math.min(min.z, lo.z);
        max.x = Math.max(max.x, hi.x); max.y = Math.max(max.y, hi.y); max.z = Math.max(max.z, hi.z);
        any = true;
    }
    if (!any) return { min: Vector3.Zero(), max: new Vector3(0.001, 0.001, 0.001) };
    return { min, max };
}

/**
 * Render an isolated, auto-framed RTT capture of a transform node's subtree.
 *
 * This is the shared "rig" behind both `generateAssetThumbnail` and
 * `captureObjectThumbnail`: a dedicated camera + light + RTT, with the render
 * list restricted to the subtree's meshes. The live `scene.activeCamera` is
 * restored synchronously, so the visible viewport is never disturbed and the
 * render loop never needs to be paused — unlike `captureSceneThumbnail`, which
 * renders the whole scene from the live camera and forces callers to hide other
 * meshes / move the camera to isolate one object.
 *
 * By default renders whatever world matrices the meshes currently have. For a
 * *posed* capture (rendering a state the live object is NOT currently in), pass
 * `pose`: it runs AFTER the RTT is ready and returns a `restore` fn. The whole
 * pose → render → restore sequence runs **synchronously** (no `await` between),
 * so the live render loop — which only ticks on the next animation frame — never
 * observes the temporary pose. The RTT framebuffer holds the posed render; the
 * async `readPixels()` afterwards reads it back after the live pose is restored.
 *
 * @param root  - The subtree root whose meshes to capture (e.g. `SceneObject.transform`)
 * @param scene - The Babylon.js scene the node lives in
 * @param size  - Square output size in pixels
 * @param label - Diagnostic label for the RTT-readiness wait
 * @param opts  - `pose`: optional synchronous LOGICAL posing hook — apply a state
 *                change (setState / goTo / pushContribution) and return a `restore`
 *                fn that reverts it. The viewer wraps it to force the subtree's
 *                world matrices after apply and after restore, and runs the whole
 *                apply→render→restore window synchronously.
 *                **Contract — the hook MUST be synchronous in effect:** the apply
 *                and the subsequent `rtt.render()` run with NO `await` between them,
 *                so the pose's transform mutations must land before the hook returns
 *                (e.g. `setState(name, true)` / `goTo(name, false)` snap instantly;
 *                an `await`-first driver would render the wrong pose). The hook is
 *                also called more than once — bounds-measure, then once per render
 *                attempt (one more per frame the posed meshes are not ready, up to
 *                `RTT_READY_MAX_FRAMES`), each apply restored before any yield — so
 *                it must be idempotent: drivers that seed per-call state (e.g.
 *                `enterFrom` from the prior keyframe) must capture+restore that in
 *                their `restore` fn rather than relying on re-navigation.
 *                `matchEditorCamera`: when the live `scene.activeCamera` is
 *                an ArcRotateCamera, shoot from its alpha/beta/fov so the thumbnail
 *                matches the editor view (front, upright) instead of a fixed angle.
 *                `injectLight`: add a temporary HemisphericLight — only needed for
 *                freshly-imported assets whose scene lighting may not be applied yet;
 *                AVOID for live captures (it brightens the live viewport during the
 *                async readiness wait → a visible flash).
 */
async function captureTransformSubtree(
    root: TransformNode,
    scene: Scene,
    size: number,
    label: string,
    opts: { pose?: () => () => void; matchEditorCamera?: boolean; injectLight?: boolean; frame?: CaptureFrame } = {},
): Promise<File | null> {
    const { pose, matchEditorCamera = false, injectLight = false, frame } = opts;
    // Every candidate mesh in the subtree. Which of these is actually VISIBLE is
    // pose-dependent — a keyframe/state can toggle `active` (→ isEnabled), so the
    // posed visibility may differ from the live one. We therefore must NOT filter
    // by the live enablement here; the posed-visible subset is collected inside
    // the pose window below (`visibleMeshes`).
    const candidateMeshes = root.getChildMeshes();
    if (candidateMeshes.length === 0) return null;

    // Wrap the caller's logical pose hook so the viewer owns the world-matrix
    // recompute: after applying the pose (and after restoring), the subtree's
    // world matrices are forced so bounds/render reflect the new transforms (the
    // live render loop hasn't ticked). The caller's hook only changes logical
    // state (setState / goTo / pushContribution); it must be synchronous.
    //
    // For a posed capture the hook is applied at least TWICE: once here to
    // measure the POSED bounds + collect the POSED-visible meshes (then restored
    // before the async readiness wait), and again for the render, once per
    // attempt in `renderPosedWhenReady` until the posed meshes are ready. Both
    // are required — measuring un-posed would frame the wrong pose AND collect
    // the wrong visibility set; rendering un-posed would capture it — so the
    // repeated calls are intentional; don't collapse them.
    const applyPose = pose
        ? (): (() => void) => {
            const restore = pose();
            // If the post-apply recompute throws, undo the pose before propagating —
            // `pose` is arbitrary caller code and the scene must not be left dirty.
            try {
                recomputeSubtreeWorld(root);
            } catch (e) {
                restore();
                throw e;
            }
            return () => { restore(); recomputeSubtreeWorld(root); };
        }
        : undefined;

    // Apply the pose (if any), then — INSIDE the posed window — collect the
    // posed-visible meshes and measure their bounds. Filtering visibility here
    // (not before the pose) is the fix for posed captures whose pose toggles a
    // mesh's `active`/enablement: capturing Base while the live state has an
    // object deactivated would otherwise drop that object from the render list
    // (and vice-versa). Guarded so a throw between apply and restore can't leave
    // the live scene in the temporary pose.
    let restoreForMeasure: (() => void) | undefined;
    let visibleMeshes: AbstractMesh[];
    let min: Vector3;
    let max: Vector3;
    try {
        if (applyPose) restoreForMeasure = applyPose();
        visibleMeshes = candidateMeshes.filter(isCapturableMesh);
        // An empty posed set means there's nothing to render: bounds would be
        // degenerate (±MAX_VALUE → Infinite radius). Early-out with null and let
        // the caller show a placeholder. (Returning inside the try is intentional:
        // `finally` still runs `restoreForMeasure` before we leave, so the pose
        // never leaks past this early-out.)
        if (visibleMeshes.length === 0) {
            return null;
        }
        ({ min, max } = computeMeshesWorldBounds(visibleMeshes));
    } finally {
        restoreForMeasure?.();
    }
    const center = min.add(max).scaleInPlace(0.5);
    const extents = max.subtract(min);
    // Bounding-sphere radius (half the diagonal) is what must fit in frame.
    const boundRadius = Math.max(extents.length() * 0.5, 0.001);

    // Create isolated RTT rig — restore the editor camera immediately so the
    // visible viewport never changes.
    const editorCam = scene.activeCamera;

    // Orbit angle: match the editor camera (so the shot is front/upright like the
    // viewport) when asked + available, else a sensible fixed 3/4 angle.
    const editorArc = (matchEditorCamera && editorCam instanceof ArcRotateCamera) ? editorCam : null;
    // A framed capture's view replaces the editor camera's angles; the editor camera is only read.
    const orbit = frame?.view ? viewOrbit(frame.view) : null;
    const alpha = orbit ? orbit.alpha : editorArc ? editorArc.alpha : -Math.PI / 4;
    const beta = orbit ? orbit.beta : editorArc ? editorArc.beta : Math.PI / 3;
    const fov = editorArc ? editorArc.fov : 0.8;

    // Camera distance. The live path (matchEditorCamera) fits the bounding SPHERE
    // to the square RTT for the camera's FOV — distance = r / sin(fov/2) — with
    // padding, so the object is always fully framed regardless of FOV. The headless
    // asset path keeps its original heuristic (orbit radius = 0.75 × diagonal) so
    // catalog thumbnails render exactly as before.
    // A framed capture fits the box itself to its frame's aspect, so the content fills the frame.
    const FRAME_PADDING = 1.25;
    const distance = frame
        ? framedDistance(min, max, center, alpha, beta, fov, frame.width / frame.height)
        : editorArc
            ? (boundRadius / Math.sin(fov / 2)) * FRAME_PADDING
            : Math.max(extents.length() * 0.75, 0.1);

    const cam = new ArcRotateCamera('__thumb_cam__', alpha, beta, distance, center, scene);
    cam.fov = fov;
    if (editorArc || frame) {
        // Live and framed paths: tight clip planes bracketing the bounding sphere.
        cam.minZ = Math.max(distance - boundRadius * 4, 0.01);
        cam.maxZ = distance + boundRadius * 4;
    } else {
        // Headless asset path: preserve the ORIGINAL generous far plane
        // (`radius * 20`, radius = 0.75 × diagonal) so spatially-extended catalog
        // assets aren't clipped — the tight live-path planes would cut them.
        cam.minZ = 0.01;
        cam.maxZ = distance * 20;
    }
    scene.activeCamera = editorCam; // restore immediately

    // Only inject a light for headless asset previews (see opts.injectLight) — for
    // live captures the scene already has lighting and an extra light would flash
    // the visible viewport during the async readiness wait.
    const light = injectLight ? new HemisphericLight('__thumb_light__', new Vector3(0.3, 1, 0.5), scene) : null;
    if (light) light.intensity = 1.2;

    const renderSize = captureRenderSize(scene, Math.min(size * SUPERSAMPLE, MAX_SUBTREE_RENDER), size);
    const rtt = new RenderTargetTexture(
        '__thumb_rtt__',
        { width: renderSize, height: renderSize },
        scene,
        false,
        false,
        Constants.TEXTURETYPE_UNSIGNED_BYTE,
    );
    rtt.activeCamera = cam;
    rtt.clearColor = new Color4(0, 0, 0, 0);
    rtt.renderParticles = true;
    rtt.ignoreCameraViewport = true;
    const disposeQuality = configureCaptureQuality(rtt, scene);

    // Populate render list with the subtree's visible meshes only
    rtt.renderList = visibleMeshes;
    // Subtree capture: only emitters that are the root or a descendant of it.
    const { systems: particleSystems, restore: restoreParticles } = makeParticleEmittersRenderable(
        scene,
        rtt.renderList,
        (emitter) => emitter === root || emitter.isDescendantOf(root),
    );
    // Same subtree scoping for text (see addTextDrawPass). The draw runs
    // inside rtt.render(), i.e. within the synchronous pose window, so
    // renderGlyphs' enabled-check sees the POSED state — a pose that toggles
    // a text node's `active` shows/hides its glyphs like it does its meshes.
    const textSources = textSourcesInSubtree(scene, root);
    const removeTextPass = addTextDrawPass(rtt, cam, () => textSources);

    // Freeze projection with aspect=1 for the square RTT
    cam.freezeProjectionMatrix(
        captureProjection(cam.fov, cam.minZ, cam.maxZ, visibleMeshes, scene.getEngine().isNDCHalfZRange),
    );

    try {
        await waitForRttReady(rtt, scene, label,
            () => textSources.every((s) => s.isCaptureReady()));
        // Pose → prewarm → render → restore, all SYNCHRONOUS (no await between) so
        // the live render loop never sees the temporary pose. `rtt.render()` is
        // synchronous and writes the posed frame into the RTT framebuffer; restore
        // returns the live nodes to their original pose before any yield. Prewarm
        // runs AFTER the pose so particles emit from the posed emitter position.
        const render = (): void => {
            const restorePrewarm = prewarmParticleSystems(particleSystems, applyPose !== undefined);
            rtt.render();
            restorePrewarm();
        };
        if (applyPose) await renderPosedWhenReady(rtt, scene, label, applyPose, render);
        else render();
        const pixels = await rtt.readPixels();
        if (pixels && frame) {
            const band = cropFramePixels(pixels, renderSize, frame);
            return await pixelsToSizedFile(band.pixels, band.width, band.height, frame.width, frame.height);
        }
        if (!pixels) return null;
        return await pixelsToFile(pixels, renderSize, renderSize, size);
    } finally {
        removeTextPass();
        restoreParticles();
        disposeQuality();
        rtt.dispose();
        light?.dispose();
        cam.dispose();
    }
}

/**
 * Run `render` inside the pose window once the POSED render list is ready.
 *
 * `waitForRttReady` runs on the live pose, and Babylon's readiness check skips
 * disabled meshes. A pose can show meshes the live scene hides (another page,
 * a state that enables a child). A shader that never compiled because its mesh
 * was never drawn passes that wait, and the render then silently skips the
 * submesh: the card of a hidden page came out without its material. So check
 * again inside the pose window and render in that same window. When something
 * is not ready, restore, wait a frame (the check has started the compile) and
 * retry, bounded by `RTT_READY_MAX_FRAMES` with the same render-anyway
 * contract. Every apply is restored before any yield, so the live render loop
 * never sees the pose.
 */
async function renderPosedWhenReady(
    rtt: RenderTargetTexture,
    scene: Scene,
    label: string,
    applyPose: () => () => void,
    render: () => void,
): Promise<void> {
    for (let frames = 0; ; frames++) {
        const restore = applyPose();
        let ready: boolean;
        try {
            ready = rtt.isReadyForRendering();
        } catch (e) {
            restore();
            throw e;
        }
        if (ready || frames >= RTT_READY_MAX_FRAMES) {
            if (!ready) {
                Logger.warn(`[ThumbnailCapture] RTT '${label}' posed meshes not ready after ${RTT_READY_MAX_FRAMES} frames, rendering anyway`);
            }
            try {
                render();
            } finally {
                restore();
            }
            return;
        }
        restore();
        await new Promise<void>((resolve) => scene.onBeforeRenderObservable.addOnce(() => resolve()));
    }
}

/** Ready every state-bound material under `object` for a posed capture (see Renderer.prepareBoundMaterialVariants). */
async function prepareSubtreeMaterials(object: SceneObject): Promise<void> {
    const renderers = [object, ...object.getDescendants()]
        .map((o) => o.getBehavior(Renderer))
        .filter((r): r is Renderer => r !== null);
    await Promise.all(renderers.map((r) => r.prepareBoundMaterialVariants().catch(() => {})));
}

/**
 * Snapshot of a transform node's local pose, for synchronous restore. Captures
 * BOTH rotation representations + whether `rotationQuaternion` was present, so
 * restore can return the node to its exact prior state — never forcing a
 * quaternion onto a node that was using Euler `rotation` (which would persist
 * after the capture and confuse code that branches on `rotationQuaternion != null`).
 */
interface PoseSnapshot {
    position: Vector3;
    rotation: Vector3;                    // Euler backup (used when no quaternion)
    rotationQuaternion: Quaternion | null;
    scaling: Vector3;
}

/** Capture the live local transform of a node (defensive clones). */
function snapshotPose(node: TransformNode): PoseSnapshot {
    return {
        position: node.position.clone(),
        rotation: node.rotation.clone(),
        rotationQuaternion: node.rotationQuaternion?.clone() ?? null,
        scaling: node.scaling.clone(),
    };
}

/** Write a pose snapshot back onto a node (in place), preserving its rotation representation. */
function restorePose(node: TransformNode, pose: PoseSnapshot): void {
    node.position.copyFrom(pose.position);
    node.rotationQuaternion = pose.rotationQuaternion?.clone() ?? null;
    if (!pose.rotationQuaternion) node.rotation.copyFrom(pose.rotation);
    node.scaling.copyFrom(pose.scaling);
}

/**
 * Force a deep world-matrix recompute over a subtree. `computeWorldMatrix(true)`
 * recurses UP to parents, never DOWN to children, so we walk the root + every
 * descendant transform node explicitly. Needed after a synchronous pose change so
 * the RTT renders the new pose (the live render loop hasn't ticked yet).
 */
function recomputeSubtreeWorld(root: TransformNode): void {
    root.computeWorldMatrix(true);
    for (const node of root.getChildTransformNodes(false)) {
        node.computeWorldMatrix(true);
    }
}

/**
 * Capture an isolated, auto-framed thumbnail of an existing live `SceneObject`'s
 * subtree without disturbing the visible viewport or pausing the render loop.
 *
 * Three modes:
 *  - **Current pose** (no `stateName` / no `pose`): captures the object exactly as
 *    it is now — live pose, applied materials, active state. Use for the active
 *    state card and for per-edit re-captures.
 *  - **Posed by state name** (`stateName`): convenience for the simple `Properties`
 *    case — temporarily applies that state via `setState(name, true)` and restores.
 *    Requires a `Properties` behavior; falls back to current-pose if absent/unknown.
 *    NOTE: this restores via `setState(savedActive)` and so is correct only for
 *    plain Properties states WITHOUT external contributions. For keyframes / Pages /
 *    any driver that poses via contributions or targets descendants, pass `pose`.
 *  - **Posed by callback** (`pose`): driver-agnostic. The caller applies a logical
 *    pose (e.g. `Keyframes.goTo(name, false)`, `pushContribution`) and returns a
 *    `restore` fn. The viewer guarantees the no-yield window + world-matrix recompute
 *    and never disturbs the live viewport. This is the general form; `stateName` is a
 *    thin wrapper over it.
 *
 * In all posed modes the apply→capture→restore runs synchronously, so the live
 * render loop (which only ticks on the next animation frame) never sees the temporary
 * pose — no shuffle, no flash. Returns a PNG `File`, or undefined on failure.
 *
 * **`pose` callback contract (read before writing one):**
 *  - It must be **synchronous in effect** — the apply and the RTT render run with NO
 *    `await` between, so the transform mutation must land before the hook returns
 *    (`setState(_, true)` / `goTo(_, false)` snap instantly; an `await`-first driver
 *    would capture the wrong pose). State-bound materials under `object` are loaded
 *    and compiled before the window opens, so a state that swaps a material GLB
 *    shows it.
 *  - It must be **atomic on failure** — if the apply throws, the hook owns reverting any
 *    partial mutation it made. The viewer reverts what `applyPose` itself did (the
 *    world-matrix recompute), but it has no `restore` fn for a hook that threw before
 *    returning one, so a half-applied pose would leak to the live scene.
 *  - It is **called more than once** — once to measure the posed bounds (for camera
 *    framing), then once per render attempt: usually one, plus one per frame the posed
 *    meshes are not yet ready to draw, up to `RTT_READY_MAX_FRAMES`. Each apply is
 *    restored before the next, so any side effect of apply or restore (e.g.
 *    `restLoopingAnimationsPose`'s stop/play) repeats with it, and the hook must be
 *    **idempotent**. The camera is framed from the FIRST invocation's bounds. An
 *    INSTANT snap (`setState(_, true)` / `goTo(_, false)`) lands on the same target
 *    pose every time → naturally idempotent (`enterFrom` only seeds the *animated*
 *    start offset and is overwritten by the snap, so it doesn't affect an instant
 *    capture). But an ANIMATED or active-toggling hook whose pose differs across calls
 *    (e.g. seeding `enterFrom` per call) must capture+restore that per-call state in
 *    its `restore` fn, or the render may be framed from stale bounds.
 *
 * **Render list:** the subtree's meshes visible UNDER the pose (sampled in the measure
 * window), and the render waits, bounded, until they are ready to draw, so a pose that
 * shows a hidden page or child renders it with its materials.
 *
 * **Limitations:** captures are NOT serialized: concurrent captures on the
 * same object can interleave their pose/restore across the readiness `await` — callers
 * should serialize per object (the sandbox keyframe fill runs one at a time).
 *
 * @param object - The live SceneObject to capture (its `transform` subtree)
 * @param opts   - `size` = output PNG size (default 256, capped at 2048 and at the
 *                 engine's max texture size; it renders at 4x that, up to 1024, so a 256
 *                 output is a 1024 render); `stateName` OR `pose` to capture a non-current pose
 */
export async function captureObjectThumbnail(
    object: SceneObject,
    opts?: {
        size?: number;
        stateName?: string;
        pose?: () => () => void;
        /**
         * Look from this view (yaw/pitch in degrees from the scene's front) instead of the editor camera. It is
         * only read, never moved.
         */
        view?: { yaw: number; pitch: number };
        /**
         * The PNG's size in px. With `view`, `width` or `height` the capture is framed: the content's box fills
         * this aspect (rather than its bounding sphere a square). It renders at up to 1024 px on the longer side,
         * so a larger frame comes out at 1024 on that side, same aspect. Either one alone takes `size` for the
         * other. With none of `view`, `width` and `height`, the capture is the square one above.
         */
        width?: number;
        height?: number;
    },
): Promise<File | undefined> {
    const frame = captureFrameOf(opts, THUMB_SIZE);
    // A framed capture renders at most MAX_SUBTREE_RENDER on its longer side; its band is never scaled up.
    const size = frame ? Math.min(MAX_SUBTREE_RENDER, Math.max(frame.width, frame.height)) : opts?.size ?? THUMB_SIZE;
    const root = object.transform;

    // Resolve the synchronous LOGICAL pose hook. Explicit `pose` wins; otherwise
    // `stateName` builds a Properties-state pose hook (simple, no-contribution case).
    // The viewer (captureTransformSubtree) owns the world-matrix recompute, so these
    // hooks only change logical state + transform.
    let pose = opts?.pose;
    const stateName = opts?.stateName;
    if (!pose && stateName !== undefined) {
        const props = object.getBehavior(Properties);
        if (props && props.getStateNames().includes(stateName)) {
            pose = () => {
                const savedPose = snapshotPose(root);
                const savedPick = props.getEditorState();
                props.setEditorState(stateName, true); // instant = synchronous snap
                return () => {
                    props.setEditorState(savedPick, true); // undefined = back to the runtime slot
                    restorePose(root, savedPose); // value-exact transform restore
                };
            };
        }
    }

    try {
        // A pose can switch a slot to a state-bound material. The pose window
        // cannot yield, so load and compile those materials before it opens.
        if (pose) await prepareSubtreeMaterials(object);
        // Live capture: match the editor camera angle (front/upright like the
        // viewport) and DON'T inject a light (the scene is already lit; an extra
        // light would flash the live viewport during the async readiness wait).
        return (await captureTransformSubtree(root, object.babylonScene, size, 'captureObjectThumbnail', { pose, matchEditorCamera: true, ...(frame ? { frame } : {}) })) ?? undefined;
    } catch (err) {
        Logger.error('[ThumbnailCapture] Object thumbnail capture failed:', err);
        return undefined;
    }
}
